import React, { useState } from 'react';
import { cn } from '../../../utils/classnames';
import { formatPhone } from '../../../utils/masks';
import { CheckIcon, LinkIcon, UserPen } from 'lucide-react';
import IconButton from '../../../components/ui/buttons/IconButton';
import logoMagnet from '../../../../assets/images/logo-magnet';
import ActionButton from '../../../components/ui/buttons/ActionButton';
import { useToast } from '../../../components/ui/toast/ToastContext';
import { useConfirm } from '../../../components/ui/confirm/ConfirmContext';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';

const ProspectRow = ({ client, index, onEdit, onEditPlan, onSuccess }) => {
  const [copied, setCopied] = useState(false);

  const isEven = index % 2 === 0;
  const bgClass = isEven ? 'bg-white' : 'bg-transparent';

  const { addToast } = useToast();
  const confirm = useConfirm();

  const handleConfirmClient = async (e) => {
    e.preventDefault();

    const ok = await confirm({
      title: 'CONFIRMAR CADASTRO?',
      description: `Deseja realmente ativar o cadastro de ${client.name}?`,
      confirmText: 'SIM, CONFIRMAR',
    });

    if (!ok) return;

    fetchWithCsrf(`/app/planner/prospects/${client.id}/confirm_registration`, {
      method: 'POST',
    })
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || 'Erro ao ativar cliente.');

        addToast({ type: 'success', message: json.message || 'Cliente ativado com sucesso!' });
        onSuccess();
      })
      .catch((err) => {
        console.error('[ConfirmRegistration] Erro:', err);
        addToast({ type: 'error', message: err.message });
      });
  };

  const handlePaymentLinkClick = () => {
    if (!client.paymentLink) return;

    navigator.clipboard
      .writeText(client.paymentLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Erro ao copiar link:', err);
      });
  };

  return (
    <tr className="text-blueDarker">
      <td className={cn('pl-4 text-lg font-semibold py-4 rounded-l-xl', bgClass)}>
        <div className="flex items-center gap-2">
          <div className="truncate whitespace-nowrap" title={client.name}>
            {client.name}
          </div>
          {client.magnetCustomerId && (
            <img src={logoMagnet} alt="Magnet" className="w-4 h-4 shrink-0" title="Importado da Magnet" />
          )}
        </div>
      </td>

      <td className={cn('pl-5 text-lg', bgClass)}>
        <div className="truncate whitespace-nowrap" title={client.email}>
          {client.email}
        </div>
      </td>

      <td className={cn('text-base', bgClass)}>
        <div className="truncate whitespace-nowrap" title={formatPhone(client.phone)}>
          {formatPhone(client.phone)}
        </div>
      </td>

      <td className={cn('text-base', bgClass)}>
        <div className="truncate whitespace-nowrap" title={client.createdAt}>
          {client.createdAt}
        </div>
      </td>

      <td className={cn('pr-4 rounded-r-xl', bgClass)} style={{ width: '560px', minWidth: '520px' }}>
        <div className="flex gap-2 justify-end items-center flex-nowrap overflow-x-auto md:overflow-visible no-scrollbar">
          {/* Editar dados */}
          <IconButton onClick={onEdit} icon={UserPen} />

          {/* Consulta e plano/Aviso concluido */}
          {client.payed && client.interviewDone ? (
            <div className="flex items-center gap-2 text-tealMedium">
              <CheckIcon size={14} strokeWidth={1.5} />
              <span className="text-blueMedium text-xs font-semibold">TODAS AS ETAPAS FORAM CONCLUÍDAS!</span>
            </div>
          ) : (
            <>
              <a href={`/app/planner/client/${client.id}/profile?from=commercial.prospects`}>
                <ActionButton
                  state={client.interviewDone ? 'edit' : 'default'}
                  icon={client.interviewDone ? CheckIcon : undefined}
                >
                  {(client.interviewDone ? 'EDITAR' : 'PREENCHER') + ' CONSULTA'}
                </ActionButton>
              </a>
              <ActionButton
                state={client.planSelected ? 'edit' : 'default'}
                onClick={onEditPlan}
                icon={client.planSelected ? CheckIcon : undefined}
              >
                {(client.planSelected ? 'EDITAR' : 'CADASTRAR') + ' PLANO'}
              </ActionButton>
            </>
          )}

          {/* Link de pagamento/confirmar cadastro */}
          {client.payed && client.interviewDone ? (
            <ActionButton state={'alt'} onClick={handleConfirmClient}>
              CONFIRMAR CADASTRO
            </ActionButton>
          ) : (
            <div className="relative">
              <ActionButton
                state={client.planSelected ? 'default' : 'disabled'}
                onClick={handlePaymentLinkClick}
                icon={LinkIcon}
                title="Copiar link"
              >
                LINK DE PAGAMENTO
              </ActionButton>

              {copied && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 transform mt-2 bg-blueDarker text-white text-xs px-3 py-1 rounded z-10 whitespace-nowrap">
                  Link copiado para a área de transferência!
                  <div className="absolute -top-1 left-1/2 -translate-x-1 transform w-2 h-2 bg-blueDarker rotate-45"></div>
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ProspectRow;
